import { DisabledIcon, OffIcon, OnIcon } from '../../../assets';
import { FunctionalComponent, h } from 'preact';
import { ToggleButton, ToggleContainer, ToggleLabel } from './Toggle.styles';
import { useEffect, useState } from 'preact/hooks';
import { v4 as uuidv4 } from 'uuid';

type Props = {
  ariaLabel?: string;
  checked?: boolean;
  hideToggle?: boolean;
  id?: string | number;
  isDisabled?: boolean;
  label?: string | JSX.Element;
  onToggle: (checked: boolean) => void;
  size?: string;
};

const Toggle: FunctionalComponent<Props> = ({
  ariaLabel = '',
  checked = false,
  hideToggle = false,
  id = uuidv4(),
  isDisabled = false,
  label,
  onToggle,
  size = 'regular',
}) => {
  const [activeElement, setActiveElement] = useState<Element | null | undefined>(null);

  const onToggleHandler = (): void => {
    if (!isDisabled) {
      let focusedToggle = null;
      try {
        focusedToggle = document.activeElement?.shadowRoot?.querySelector(':focus-visible');
      } catch (error) {
        focusedToggle = null;
      }

      if (focusedToggle) {
        setActiveElement(focusedToggle);
      }
      onToggle(!checked);
    }
  };

  useEffect(() => {
    if (activeElement) {
      (activeElement as HTMLElement).focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  return (
    <ToggleContainer>
      {label && (
        <ToggleLabel key={id} htmlFor={`toggle-${id}`} hideToggle={hideToggle}>
          {label}
        </ToggleLabel>
      )}
      {!hideToggle &&
        (isDisabled ? (
          <ToggleButton
            tabIndex={-1}
            id={`toggle-${id}`}
            size={size}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(typeof label === 'string' ? { 'aria-label': label } : {})}
            role="switch"
            aria-checked={checked ? 'true' : 'false'}
            aria-readonly={isDisabled ? 'true' : 'false'}
            aria-label={ariaLabel}
          >
            <DisabledIcon />
          </ToggleButton>
        ) : (
          <ToggleButton
            data-testid={`uc-toggle-${id}`}
            id={`toggle-${id}`}
            onClick={onToggleHandler}
            size={size}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...(typeof label === 'string' ? { 'aria-label': label } : {})}
            role="switch"
            aria-checked={checked ? 'true' : 'false'}
            aria-readonly={isDisabled ? 'true' : 'false'}
            aria-label={ariaLabel}
          >
            {checked ? <OnIcon /> : <OffIcon />}
          </ToggleButton>
        ))}
    </ToggleContainer>
  );
};

export default Toggle;
